import type {
  Permission,
  WorkspaceInviteLinkExpireTime,
} from '@affine/graphql';
import { Service } from '@toeverything/infra';

import type { WorkspaceService } from '../../workspace';
import { WorkspaceMembers } from '../entities/members';
import type { WorkspaceMembersStore } from '../stores/members';

export class WorkspaceMembersService extends Service {
  constructor(
    private readonly store: WorkspaceMembersStore,
    private readonly workspaceService: WorkspaceService
  ) {
    super();
  }

  members = this.framework.createEntity(WorkspaceMembers);

  async inviteMembers(
    emails: string[],
    options?: {
      permission?: Permission;
      isGuest?: boolean;
      docId?: string;
      folderId?: string;
      ttlHours?: number;
    }
  ) {
    return await this.store.inviteBatch(
      this.workspaceService.workspace.id,
      emails,
      options
    );
  }

  async generateInviteLink(expireTime: WorkspaceInviteLinkExpireTime) {
    return await this.store.generateInviteLink(
      this.workspaceService.workspace.id,
      expireTime
    );
  }

  async revokeInviteLink() {
    return await this.store.revokeInviteLink(
      this.workspaceService.workspace.id
    );
  }

  async revokeMember(userId: string) {
    return await this.store.revokeMemberPermission(
      this.workspaceService.workspace.id,
      userId
    );
  }

  async approveMember(userId: string) {
    return await this.store.approveMember(
      this.workspaceService.workspace.id,
      userId
    );
  }

  async adjustMemberPermission(userId: string, permission: Permission) {
    return await this.store.adjustMemberPermission(
      this.workspaceService.workspace.id,
      userId,
      permission
    );
  }

  async transferWorkspaceOwnership(userId: string) {
    return await this.store.transferWorkspaceOwnership(
      this.workspaceService.workspace.id,
      userId
    );
  }

  async fetchAllGuests() {
    const result = await this.store.fetchMembers(
      this.workspaceService.workspace.id,
      0,
      200
    );
    return result.members.filter((m: { isGuest: boolean }) => m.isGuest);
  }
}
